import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';

interface DemoConsentModalProps {
  onConsent: () => void;
  onDecline: () => void;
}

/**
 * Demo Consent Modal
 * Shown before starting verification in demo mode
 * Provides legal protection and informed consent
 */
export default function DemoConsentModal({ onConsent, onDecline }: DemoConsentModalProps) {
  const [isChecked, setIsChecked] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg border border-amber-500/30 max-w-md w-full p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-bold text-white">Demo Mode Consent</h2>
            <p className="text-sm text-gray-400 mt-1">Please read before proceeding</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-gray-800/50 rounded p-4 space-y-3 text-sm text-gray-300">
          <div>
            <p className="font-semibold text-amber-300 mb-1">This is a Demo Environment</p>
            <p>Agos is currently in demo mode. NPC registration is pending.</p>
          </div>

          <div>
            <p className="font-semibold text-blue-300 mb-1">Data Handling</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Your data will be deleted within 24 hours</li>
              <li>No permanent storage of ID images</li>
              <li>Demo purposes only</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-green-300 mb-1">Your Rights</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>You can request data deletion anytime</li>
              <li>No commercial use of your data</li>
              <li>Full compliance with RA 10173 (Data Privacy Act)</li>
            </ul>
          </div>
        </div>

        {/* Checkbox */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            className="w-4 h-4 mt-1 rounded border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-300">
            I understand this is a demo environment and consent to temporary processing of my data for demonstration purposes only.
          </span>
        </label>

        {/* Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={onDecline}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-800 transition-colors font-medium"
          >
            Decline
          </button>
          <button
            onClick={onConsent}
            disabled={!isChecked}
            className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4" />
            I Consent
          </button>
        </div>

        {/* Footer */}
        <p className="text-xs text-gray-500 text-center">
          For more information, see our <a href="/privacy-policy" className="text-blue-400 hover:underline">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
}